import { MediaElementProvider } from '@optools/components/media/MediaElementProvider/media-element-provider';
import { TeamSpot } from '@optools/components/team-spot';
import { Badge } from '@optools/components/ui/badge';
import { Button } from '@optools/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { cn } from '@optools/components/ui/utils';
import {
  LoggerGameContent,
  LoggerGamePipeline,
  LoggerGameTeam,
} from '@optools/services/operator-gateway/logger/types';
import {
  Content,
  Feed,
  MediaContext,
  MediaFeedType,
  MediaType,
  PipelineStatus,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { selectLoggerConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { getMediaId, hasVideoMedia } from '@optools/utils/pipeline';
import { Bug, BugOff, DollarSign, Rss } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { determineNotificationPosition } from '../utils/notification';
import { AdControls } from './ad-controls';
import { FeedInfoPanel } from './feed-info-panel';
import { GameStateControls } from './game-state-controls';
import { ListenLines } from './listen-lines';
import { LoggerRed5Players } from './logger-red5-players';
import { SlateControls } from './slate-controls';
import { SyncBroadcastButton } from './sync-broadcast-button';
import { VideoPanel } from './video-panel';

export interface FeedConsoleProps {
  content: LoggerGameContent[];
  feed?: Feed;
  pipeline: LoggerGamePipeline;
  side: MediaFeedType;
  teamData?: LoggerGameTeam;
}

export interface NetworkDisplayProps {
  content: LoggerGameContent;
}

function NetworkDisplay({ content }: NetworkDisplayProps) {
  const callSign = content.callSign;
  return callSign ? (
    <p className="font-bold">{callSign}</p>
  ) : (
    <p className="font-bold">Team: NATIONAL/UNKNOWN</p>
  );
}

interface FeedConsoleTooltipProps {
  active?: boolean;
  icon: React.ReactNode;
  onClickHandler: () => void;
  tooltipLabel: string;
}

function FeedConsoleTooltipButton({
  active = false,
  icon,
  onClickHandler,
  tooltipLabel,
}: FeedConsoleTooltipProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          aria-label={tooltipLabel}
          className={cn('size-6', active && 'text-blue-500')}
          size="icon"
          variant="ghost"
          onClick={onClickHandler}>
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p className="text-xs">{tooltipLabel}</p>
      </TooltipContent>
    </Tooltip>
  );
}

type MediaItem = MediaContext & Pick<Content, 'callSign'>;

/**
 * Weights the media items based on their type as follows
 * 1. National Video Feed
 * 2. Other Video Feeds
 * 3. English Audio
 * 4. Alternative language audio
 *
 * @param item the media item to be scored
 * @returns A positive number indicating the weighted score of the MediaItem
 */
const getFeedScore = (item: MediaItem) => {
  let score = 0;

  if (item.mediaType === MediaType.VIDEO) {
    score += 1;
    if (item.feedType === MediaFeedType.NATIONAL) {
      score += 0.5;
    }
  } else {
    if (item.language === 'en') {
      score += 0.5;
    }
  }

  return score;
};

export function FeedConsole({ content, feed, pipeline, side, teamData }: FeedConsoleProps) {
  const isVideoPipeline = hasVideoMedia(pipeline);
  const { commercialSlateCode, defaultPlaybackExperience, red5 } =
    useAppSelector(selectLoggerConfig);

  const hasProgramSource = useRef<boolean>(false);
  const [daiModeEnabled, setDaiModeEnabled] = useState(true);
  const [red5ModeEnabled, setRed5ModeEnabled] = useState(() => {
    return isVideoPipeline && red5.enabled && defaultPlaybackExperience === 'Red5';
  });
  const [debugModeEnabled, setDebugModeEnabled] = useState(false);
  const [mediaId, setMediaId] = useState(() => getMediaId(pipeline));
  const { activeSlateCode, programSource, status } = pipeline;

  const activePipeline = ![PipelineStatus.ENDED, PipelineStatus.ENDING].includes(status);
  const disableAdControls = !activePipeline || [PipelineStatus.ARCHIVED].includes(status);
  const disableSlateControls = disableAdControls || activeSlateCode === commercialSlateCode;

  const handleClickEnableDebugMode = () => {
    setDebugModeEnabled(!debugModeEnabled);
  };

  const handleRed5PlayerFailure = useCallback(() => {
    toast.error('Red5 Experience Degraded', {
      description: 'Please revert to the HLS Player to continue logging.',
      duration: Infinity,
      position: determineNotificationPosition(side),
    });
  }, [side]);

  useEffect(() => {
    if (!programSource) return;
    if (!hasProgramSource.current) {
      hasProgramSource.current = true;
      return;
    }
    toast.warning('Program source switch', {
      description: `Program source switch to ${programSource}`,
      duration: Infinity,
      position: determineNotificationPosition(side),
    });
  }, [programSource, side]);

  const sortedMedia = useMemo(() => {
    return [...pipeline.media]
      .reduce<MediaItem[]>((acc, item) => {
        const contentItem = content.find((contentItem) => contentItem.mediaId === item.id);
        if (contentItem) {
          acc.push({ ...item, callSign: contentItem.callSign });
        }
        return acc;
      }, [])
      .sort((a, b) => getFeedScore(b) - getFeedScore(a));
  }, [pipeline, content]);

  const contentItem = content.find((item) => item.mediaId === mediaId);

  if (!pipeline || !contentItem)
    return <h3 className="text-4xl font-medium">Pipeline Not Available</h3>;

  if (!activePipeline) return <h3 className="text-4xl font-medium">Pipeline Not Active</h3>;

  return (
    <div
      className="flex w-full max-w-[540px] flex-col items-center"
      data-testid={`logger-pipeline-${pipeline.id}-${mediaId}`}>
      <div className="mb-2 flex w-full justify-between gap-1">
        <div className="flex items-center gap-0.5">
          <div className="flex items-center">
            {teamData ? <TeamSpot team={teamData} /> : <NetworkDisplay content={contentItem} />}
            <p className="pl-2 text-xs">({contentItem.callSign})</p>
          </div>
          <FeedInfoPanel feed={feed} mediaId={mediaId} pipeline={pipeline} side={side} />
          <FeedConsoleTooltipButton
            active={debugModeEnabled}
            icon={debugModeEnabled ? <BugOff size={16} /> : <Bug size={16} />}
            tooltipLabel="Debug Mode"
            onClickHandler={handleClickEnableDebugMode}
          />
          {isVideoPipeline && (
            <FeedConsoleTooltipButton
              active={daiModeEnabled}
              icon={<DollarSign size={16} />}
              tooltipLabel="Google DAI"
              onClickHandler={() => setDaiModeEnabled((current) => !current)}
            />
          )}
          {red5.enabled && (
            <FeedConsoleTooltipButton
              active={red5ModeEnabled}
              icon={<Rss size={16} />}
              tooltipLabel="Red5 Player"
              onClickHandler={() => setRed5ModeEnabled((current) => !current)}
            />
          )}
          {!red5ModeEnabled && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Select value={mediaId} onValueChange={setMediaId}>
                    <SelectTrigger aria-label="Media Source" className="ml-1 text-xs" size="sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedMedia.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {`(${item.callSign}): ${item.language} ${item.mediaType.toLocaleLowerCase()}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">Media Source</TooltipContent>
            </Tooltip>
          )}
        </div>

        {isVideoPipeline && <Badge className="self-center">{pipeline.programSource}</Badge>}
      </div>

      {red5ModeEnabled ? (
        <LoggerRed5Players
          config={pipeline.red5Config}
          debugMode={debugModeEnabled}
          onFailure={handleRed5PlayerFailure}
        />
      ) : (
        <MediaElementProvider>
          <VideoPanel
            daiMode={daiModeEnabled}
            debugMode={debugModeEnabled}
            feed={feed}
            mediaId={mediaId}
            pipeline={pipeline}
            side={side}
          />
        </MediaElementProvider>
      )}

      <div className="my-4 flex justify-center gap-4">
        {isVideoPipeline && (
          <div className="flex basis-1/2 justify-center">
            <AdControls
              debugMode={debugModeEnabled}
              disabled={disableAdControls}
              mediaId={mediaId}
              pipeline={pipeline}
              side={side}
            />
          </div>
        )}
        <div className="flex basis-1/2 justify-center">
          <GameStateControls
            debugMode={debugModeEnabled}
            mediaId={mediaId}
            pipeline={pipeline}
            side={side}
          />
        </div>
      </div>
      {activePipeline && isVideoPipeline ? (
        <SlateControls
          debugMode={debugModeEnabled}
          disabled={disableSlateControls}
          pipeline={pipeline}
          side={side}
        />
      ) : null}
      <div className="flex w-100 justify-center gap-6 p-4">
        <SyncBroadcastButton mediaId={mediaId} side={side} />
        <ListenLines team={teamData} />
      </div>
    </div>
  );
}
